import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import {
  Stethoscope, Clock, Calendar, Activity, LogOut, RefreshCw,
  CheckCircle, AlertTriangle, Video, Pill, MessageCircle,
  ChevronDown, Send, X
} from 'lucide-react';

const SEVERITY_CONFIG = {
  urgent: { label: 'Urgent', color: 'text-red-600', bg: 'bg-red-50' },
  high:   { label: 'High',   color: 'text-orange-600', bg: 'bg-orange-50' },
  medium: { label: 'Medium', color: 'text-amber-600', bg: 'bg-amber-50' },
  low:    { label: 'Low',    color: 'text-slate-500', bg: 'bg-slate-50' },
};

const STATUS_CONFIG = {
  'Stable':           { color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle },
  'Under monitoring': { color: 'text-amber-700',   bg: 'bg-amber-50',   border: 'border-amber-200',   icon: Activity },
  'Needs attention':  { color: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',     icon: AlertTriangle },
  'Being monitored':  { color: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200',    icon: Activity },
};

/**
 * CaregiverDashboard — read-only family member view.
 * Supports multiple patients via a patient selector.
 */
const CaregiverDashboard = () => {
  const { user, logout } = useAuth();

  const [patients, setPatients] = useState([]);        // list of linked patients
  const [activePatientId, setActivePatientId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPatientPicker, setShowPatientPicker] = useState(false);

  // Message modal
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Load patient list first, then load dashboard for first patient
  useEffect(() => {
    api.get('/caregiver/my-patients')
      .then(res => {
        const list = res.data.data || [];
        setPatients(list);
        const firstId = list[0]?.patientId || null;
        setActivePatientId(firstId);
        return firstId ? api.get(`/caregiver/dashboard?patientId=${firstId}`) : null;
      })
      .then(res => { if (res) setData(res.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const switchPatient = async (patientId) => {
    setShowPatientPicker(false);
    if (patientId === activePatientId) return;
    setLoading(true);
    setActivePatientId(patientId);
    try {
      const res = await api.get(`/caregiver/dashboard?patientId=${patientId}`);
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!activePatientId) return;
    setLoading(true);
    try {
      const res = await api.get(`/caregiver/dashboard?patientId=${activePatientId}`);
      setData(res.data.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await api.post('/caregiver/message', { message });
      toast.success('Message sent');
      setMessage('');
      setShowMessage(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatLastActive = (hours) => {
    if (hours === null || hours === undefined) return 'No recent activity';
    if (hours < 1) return 'Active recently';
    if (hours < 24) return `Last checked in ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `Last checked in ${days} day${days > 1 ? 's' : ''} ago`;
  };

  const formatApptDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) +
      ' at ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const patient = data?.patient;
  const status = data?.status;
  const appt = data?.upcomingAppointment;
  const recentSymptoms = data?.recentSymptoms || [];
  const medications = data?.medications || [];
  const sc = STATUS_CONFIG[status?.riskLabel] || STATUS_CONFIG['Being monitored'];
  const StatusIcon = sc.icon;
  const activePatientName = patients.find(p => p.patientId === activePatientId)?.patientName || patient?.name;

  if (loading) return (
    <div className="min-h-screen bg-[#f9f9fd] flex items-center justify-center">
      <div className="space-y-4 w-full max-w-sm px-4">
        {[1, 2, 3].map(i => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse border border-slate-100" />)}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f9f9fd]">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-black text-primary tracking-tighter">SymptoConnect</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleRefresh} className="p-2 rounded-xl hover:bg-slate-100 transition-all">
            <RefreshCw className="w-4 h-4 text-slate-400" />
          </button>
          <button onClick={logout} className="p-2 rounded-xl hover:bg-slate-100 transition-all">
            <LogOut className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-8">

        {/* Greeting + patient selector */}
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">
            Hi, {user?.name?.split(' ')[0]}
          </h1>
          {patients.length > 1 ? (
            <div className="relative mt-1 inline-block">
              <button
                onClick={() => setShowPatientPicker(p => !p)}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
              >
                Viewing <strong>{activePatientName}</strong>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showPatientPicker && (
                <div className="absolute top-7 left-0 bg-white border border-slate-100 rounded-xl shadow-lg z-20 min-w-[180px] py-1">
                  {patients.map(p => (
                    <button
                      key={p.patientId}
                      onClick={() => switchPatient(p.patientId)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${p.patientId === activePatientId ? 'font-bold text-primary' : 'text-slate-700'}`}
                    >
                      {p.patientName}
                      <span className="text-xs text-slate-400 ml-1 capitalize">({p.relationship})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500 mt-0.5">
              You're a caregiver for <strong>{activePatientName}</strong>
            </p>
          )}
        </div>

        {!data ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
            <p className="text-slate-500 text-sm">Could not load patient status. Please try again.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 items-start">

            {/* ── Left column: status + check-in + message + appointment ── */}
            <div className="space-y-4 lg:col-span-1">

              {/* Status card */}
              <div className={`bg-white rounded-2xl border ${sc.border} p-5 space-y-3`}>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Current Status</p>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl ${sc.bg} flex items-center justify-center shrink-0`}>
                    <StatusIcon className={`w-6 h-6 ${sc.color}`} />
                  </div>
                  <div>
                    <p className={`text-lg font-extrabold ${sc.color}`}>{status?.riskLabel}</p>
                    <p className="text-xs text-slate-500">Care team is monitoring {patient?.name}</p>
                  </div>
                </div>
                {status?.daysSinceDischarge != null && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 pt-1 border-t border-slate-100">
                    <Calendar className="w-3.5 h-3.5" />
                    Day {status.daysSinceDischarge} since last discharge
                  </div>
                )}
              </div>

              {/* Last check-in */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Last Check-In</p>
                  <p className="text-sm font-bold text-slate-900">{formatLastActive(patient?.hoursSinceLastActive)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {patient?.lastActive
                      ? new Date(patient.lastActive).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : 'No data yet'}
                  </p>
                </div>
                {patient?.hoursSinceLastActive != null && patient.hoursSinceLastActive < 24 && (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-bold text-emerald-600">Active</span>
                  </div>
                )}
              </div>

              {/* Message patient */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5">
                {showMessage ? (
                  <form onSubmit={handleSendMessage} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Send a message</p>
                      <button type="button" onClick={() => { setShowMessage(false); setMessage(''); }}>
                        <X className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                    <textarea
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder={`Write a message to ${patient?.name}…`}
                      className="w-full text-sm px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-slate-400">{message.length}/500</span>
                      <button type="submit" disabled={sending || !message.trim()}
                        className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50">
                        <Send className="w-3.5 h-3.5" />
                        {sending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <button onClick={() => setShowMessage(true)} className="w-full flex items-center gap-3 text-left">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                      <MessageCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Message {patient?.name}</p>
                      <p className="text-xs text-slate-400">They'll receive a notification</p>
                    </div>
                  </button>
                )}
              </div>

              {/* Upcoming appointment */}
              {appt ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Upcoming Appointment</p>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                      {appt.meetingLink ? <Video className="w-5 h-5 text-primary" /> : <Calendar className="w-5 h-5 text-primary" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-slate-900 capitalize">{appt.type?.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{formatApptDate(appt.date)}</p>
                      {appt.meetingLink && (
                        <a href={appt.meetingLink} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-primary hover:underline">
                          <Video className="w-3 h-3" /> Join virtual visit
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-slate-100 p-5 text-center">
                  <Calendar className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm font-bold text-slate-500">No upcoming appointments</p>
                </div>
              )}
            </div>

            {/* ── Right column (spans 2): symptoms + medications + disclaimer ── */}
            <div className="space-y-4 lg:col-span-2">

              {/* Recent symptom reports */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Recent Symptom Reports</p>
                {recentSymptoms.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No recent symptoms reported.</p>
                ) : (
                  <div className="divide-y divide-slate-50">
                    {recentSymptoms.map((s, i) => {
                      const sev = SEVERITY_CONFIG[s.severity] || SEVERITY_CONFIG.low;
                      return (
                        <div key={i} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                          <p className="text-sm text-slate-700 font-medium flex-1 truncate">{s.title}</p>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sev.bg} ${sev.color} shrink-0`}>
                            {sev.label}
                          </span>
                          <span className="text-[10px] text-slate-400 shrink-0 w-12 text-right">
                            {new Date(s.reportedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Medication schedule */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Pill className="w-3.5 h-3.5 text-slate-400" />
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active Medications</p>
                </div>
                {medications.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No medications on file.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {medications.map((m, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl px-3 py-2.5">
                        <p className="text-sm font-semibold text-slate-800">{m.name}</p>
                        {(m.dosage || m.frequency) && (
                          <p className="text-xs text-slate-400 text-right shrink-0">
                            {[m.dosage, m.frequency].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Disclaimer */}
              <p className="text-[11px] text-slate-400 text-center leading-relaxed px-1">
                You are viewing general health status only. For clinical questions, contact {patient?.name}'s care team directly.
              </p>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default CaregiverDashboard;
